/**
 * useSwipeGesture — Detects vertical swipe gestures on the home screen.
 *
 * - Swipe down: triggers search (Spotlight)
 * - Swipe up: triggers app switcher (multitasking)
 *
 * Uses pointer events for unified touch + mouse handling.
 * Requires a minimum distance threshold to avoid accidental triggers.
 */

import { useCallback, useRef } from 'react';

export type SwipeDirection = 'up' | 'down' | null;

interface SwipeGestureOptions {
  /** Minimum distance (px) to qualify as a swipe. Default: 60 */
  threshold?: number;
  /** Maximum horizontal drift (px) before gesture is cancelled. Default: 80 */
  maxHorizontalDrift?: number;
  /** Called when swipe down is detected */
  onSwipeDown?: () => void;
  /** Called when swipe up is detected */
  onSwipeUp?: () => void;
  /** Whether gestures are disabled (e.g. in edit mode) */
  disabled?: boolean;
}

export function useSwipeGesture({
  threshold = 60,
  maxHorizontalDrift = 80,
  onSwipeDown,
  onSwipeUp,
  disabled = false,
}: SwipeGestureOptions) {
  const startY = useRef(0);
  const startX = useRef(0);
  const tracking = useRef(false);

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (disabled) return;
      // Only track primary pointer (touch finger / left mouse)
      if (!e.isPrimary) return;
      // Ignore if started on interactive elements inside tiles
      const target = e.target as HTMLElement;
      if (target.closest('a, button, input, [data-droppable]')) return;

      startY.current = e.clientY;
      startX.current = e.clientX;
      tracking.current = true;
    },
    [disabled],
  );

  const onPointerUp = useCallback(
    (e: React.PointerEvent) => {
      if (!tracking.current || !e.isPrimary) return;
      tracking.current = false;

      const deltaY = e.clientY - startY.current;
      const deltaX = Math.abs(e.clientX - startX.current);

      // Ignore if too much horizontal movement
      if (deltaX > maxHorizontalDrift) return;

      if (deltaY > threshold && onSwipeDown) {
        onSwipeDown();
      } else if (deltaY < -threshold && onSwipeUp) {
        onSwipeUp();
      }
    },
    [threshold, maxHorizontalDrift, onSwipeDown, onSwipeUp],
  );

  const onPointerCancel = useCallback(() => {
    tracking.current = false;
  }, []);

  return {
    onPointerDown,
    onPointerUp,
    onPointerCancel,
  };
}
